"use client";

// Page de régularisation : affichée sur /pricing quand le paiement est en échec.
// Liste toutes les factures impayées qui bloquent le compte (compatible
// multi-factures), chacune réglable via sa page de paiement Stripe hébergée.

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations, useLocale } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import moment from "moment-timezone";
import { SpinnerCircular } from "spinners-react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function PastDuePanel() {
    const t = useTranslations();
    const locale = useLocale();
    const router = useRouter();
    const { update } = useSession();
    const [invoices, setInvoices] = useState(null); // null = chargement
    const [payingId, setPayingId] = useState(null);

    useEffect(() => {
        let active = true;
        fetch("/api/user/get/open-invoices")
            .then((r) => r.json())
            .then(async (d) => {
                if (!active) return;
                // Drapeau périmé, rabattu par le serveur : on relit la session
                // puis la page, qui montre alors les offres. Le chargement reste
                // affiché jusque-là.
                if (d.cleared) {
                    await update();
                    router.refresh();
                    return;
                }
                setInvoices(d.invoices || []);
            })
            .catch(() => {
                if (active) setInvoices([]);
            });
        return () => {
            active = false;
        };
        // Une seule lecture à l'ouverture de la page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const total = (invoices || []).reduce((s, i) => s + i.amount, 0);
    const anyEngaged = (invoices || []).some((i) => i.engaged);

    return (
        <div className="container mx-auto max-w-2xl px-5 py-16">
            <div className="rounded-2xl border border-red-200 bg-white p-8 dark:border-red-900/40 dark:bg-bgDark-800">
                <div className="mb-6 flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
                        <SolarIcon
                            name="danger-triangle-bold"
                            className="text-2xl text-red-500"
                        />
                    </div>
                    <div>
                        <h1 className="mb-1 text-2xl font-bold text-gray-900 dark:text-white">
                            {t("pastdue_page_title")}
                        </h1>
                        <p className="mb-0 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                            {anyEngaged
                                ? t("pastdue_page_description_engaged")
                                : t("pastdue_page_description")}
                        </p>
                    </div>
                </div>

                {invoices === null ? (
                    <div className="flex justify-center py-8">
                        <SpinnerCircular
                            size={32}
                            color="#dc2626"
                            secondaryColor="#fecaca"
                        />
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="rounded-xl bg-gray-50 p-5 text-center text-sm text-gray-500 dark:bg-bgDark-900/40 dark:text-grayDark-400">
                        {t("pastdue_page_empty")}
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {invoices.map((inv) => (
                            <li
                                key={inv.id}
                                className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 dark:border-borderDark sm:flex-row sm:items-center sm:justify-between"
                            >
                                <div className="min-w-0">
                                    <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                                        {inv.displayNameKey
                                            ? t(inv.displayNameKey)
                                            : t("pastdue_page_invoice_fallback")}
                                    </p>
                                    <p className="mb-0 text-xs text-gray-500 dark:text-grayDark-400">
                                        {t("price_euro", {
                                            amount: inv.amount.toFixed(2),
                                        })}
                                        {inv.created
                                            ? ` · ${moment
                                                  .unix(inv.created)
                                                  .locale(locale)
                                                  .format("LL")}`
                                            : ""}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPayingId(inv.id);
                                        window.location.href = inv.hostedUrl;
                                    }}
                                    disabled={payingId === inv.id}
                                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                    {payingId === inv.id ? (
                                        <SpinnerCircular
                                            size={16}
                                            color="rgba(255, 255, 255, 1)"
                                            secondaryColor="rgba(255, 255, 255, 0.2)"
                                        />
                                    ) : (
                                        t("pastdue_page_pay")
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {invoices && invoices.length > 1 && (
                    <p className="mt-4 mb-0 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        {t("pastdue_page_total", { amount: total.toFixed(2) })}
                    </p>
                )}
            </div>
        </div>
    );
}
